import {
  PlacementPositions,
  placementPositionKeys,
  platformByPositionKey,
} from '../facebook/audience/TargetingSpec/PlatformPlacements';
import { PublisherPlatform } from './PublisherPlatform';

/**
 * The two placement buckets a per-placement ad carries a cut for, in Meta's own Ads Manager
 * words. Meta can only tell these two apart: square and 4:5 serve into the same positions, so no
 * rule separates them, and no bucket is worth having for wide.
 */
export enum PlacementBucket {
  feed = 'feed',
  story = 'story',
}

export const placementBucketLabel: Record<PlacementBucket, string> = {
  [PlacementBucket.feed]: 'Feed',
  [PlacementBucket.story]: 'Story & Reels',
};

/**
 * Where the Story & Reels cut serves. Feed has no row: its rule is Meta's catch-all (an empty
 * customization spec), so every other position — and any Meta adds later — falls to the Feed cut.
 * A third bucket is a row here.
 */
export const storyAndReelsPositions: Partial<
  Record<typeof placementPositionKeys[number], readonly string[]>
> = {
  facebook_positions: ['story', 'facebook_reels'],
  instagram_positions: ['story', 'reels', 'profile_reels'],
  messenger_positions: ['story'],
};

export type PlacementCut =
  | { mediaType: 'image'; imageID: number }
  | { mediaType: 'video'; videoID: number };

/**
 * Whether a campaign's placements reach any Story & Reels position. Automatic placements do, and
 * a selected platform with no position list means all of that platform's positions.
 */
export function placementsServeStoryAndReels(
  platforms: PublisherPlatform[] | undefined,
  selection: PlacementPositions | undefined,
): boolean {
  if (!platforms?.length) return true;
  return placementPositionKeys.some((key) => {
    const storyPositions = storyAndReelsPositions[key];
    if (!storyPositions) return false;
    if (!platforms.includes(platformByPositionKey[key])) return false;
    const selected = selection?.[key] as readonly string[] | undefined;
    return (
      !selected?.length || selected.some((p) => storyPositions.includes(p))
    );
  });
}

export const PLACEMENTS_MISS_STORY_MESSAGE =
  'These ads were made for Feed and Story & Reels, but this campaign doesn’t run in Stories or Reels. Turn on a Story or Reels placement, or use different ads.';
